import type { ReactNode } from "react";
import { cx } from "../../cx";
import { Icon } from "../../rtl/glyphs";

export interface CollapsibleProps {
  readonly title: ReactNode;
  /** Current value / count shown next to the title. */
  readonly summary?: ReactNode;
  readonly defaultOpen?: boolean;
  /** Controlled open state (e.g. forced open while searching). */
  readonly open?: boolean;
  readonly onToggle?: (open: boolean) => void;
  readonly children: ReactNode;
  readonly className?: string;
  readonly testId?: string;
}

/** `<details>` wrapper: summary row = title + current value + chevron. */
export function Collapsible({
  title,
  summary,
  defaultOpen,
  open,
  onToggle,
  children,
  className,
  testId,
}: CollapsibleProps) {
  return (
    <details
      className={cx("group", className)}
      data-testid={testId}
      {...(open === undefined ? { open: defaultOpen } : { open })}
      onToggle={onToggle ? (e) => onToggle(e.currentTarget.open) : undefined}
    >
      <summary className="flex min-h-9 cursor-pointer list-none items-center gap-2 py-1 text-sm font-medium focus-visible:outline-2 focus-visible:outline-focus pointer-coarse:min-h-11 [&::-webkit-details-marker]:hidden">
        <span className="min-w-0 flex-1 truncate">{title}</span>
        {summary !== undefined && (
          <span className="text-xs font-normal text-fg-muted">{summary}</span>
        )}
        <Icon
          name="chevron-down"
          size={16}
          className="transition-transform group-open:rotate-180"
        />
      </summary>
      <div className="pt-1">{children}</div>
    </details>
  );
}
